/**
 * Per-rule histogram — client-side reduction over the loaded shadow buffer
 * (ADR-0122 §3.3).
 *
 * Slice 3 of ADR-0122 surfaces the per-rule breakdown the operator already
 * wants ("which rule is the noisy one?") without an unbounded server-side
 * aggregation. The buffer is bounded by `SHADOW_PAGE_LIMIT = 50` per fetch
 * (`Guardrails.tsx`) and the existing summary header + group-by-rule mode
 * already operate over that same buffer; the histogram is the same data
 * sliced differently.
 *
 * This module is the pure reduction half of the slice — kept out of the
 * React component so the bucket math + sort + per-decision tallying can
 * be unit-tested without jsdom. The renderer in `Guardrails.tsx` reads
 * from this helper's output via `useMemo`.
 *
 * Honesty disclosure is the renderer's concern; the helper just builds
 * the bucket array. Bars are computed against `Math.max(bucket.count)`
 * inside the renderer (the row knows its own `count` and the helper
 * gives it the others).
 *
 * Per ADR-0122 §4 must-hold 8 — purely client-only. No HTTP, no fetch,
 * no new wire field.
 */

import type { ShadowDecision } from '@sill/types';

/**
 * One bucket of the per-rule histogram.
 *
 *   - `rule_id` is the row's `rule_id` (key)
 *   - `rule_name` is the most recent `rule_name` seen for that key
 *     (the merchant may have renamed the rule between shadow writes;
 *     the latest label is the right one to display)
 *   - `count` is the total number of decisions in the bucket
 *   - `decisions` is the per-decision-kind breakdown
 *   - `latest_at` is the most-recent `evaluated_at` across the bucket
 */
export interface HistogramBucket {
  readonly rule_id: string;
  readonly rule_name: string;
  readonly count: number;
  readonly decisions: {
    readonly reject: number;
    readonly escalate: number;
  };
  readonly latest_at: string;
}

/**
 * Reduce a loaded shadow buffer into per-rule buckets sorted by count
 * DESC (then by `latest_at` DESC as a stable tiebreak).
 *
 * Pure: no side effects, no mandate bytes touched. Reads ONLY the four
 * row fields the renderer needs (`rule_id`, `rule_name`, `decision`,
 * `evaluated_at`).
 */
export function buildHistogramBuckets(
  shadow: readonly ShadowDecision[],
): HistogramBucket[] {
  // Use a mutable scratch shape for the bucket while we accumulate;
  // freeze on emit (the public type is readonly).
  interface Scratch {
    rule_id: string;
    rule_name: string;
    count: number;
    decisions: { reject: number; escalate: number };
    latest_at: string;
  }
  const map = new Map<string, Scratch>();
  for (const d of shadow) {
    const existing = map.get(d.rule_id);
    if (existing === undefined) {
      map.set(d.rule_id, {
        rule_id: d.rule_id,
        rule_name: d.rule_name,
        count: 1,
        decisions: {
          reject: d.decision === 'reject' ? 1 : 0,
          escalate: d.decision === 'escalate' ? 1 : 0,
        },
        latest_at: d.evaluated_at,
      });
    } else {
      existing.count += 1;
      if (d.decision === 'reject') existing.decisions.reject += 1;
      else if (d.decision === 'escalate') existing.decisions.escalate += 1;
      // The most-recent `rule_name` (by `evaluated_at`) wins — the
      // merchant may have renamed the rule mid-window and the latest
      // label is the operator-relevant one.
      if (d.evaluated_at > existing.latest_at) {
        existing.latest_at = d.evaluated_at;
        existing.rule_name = d.rule_name;
      }
    }
  }
  return [...map.values()].sort((a, b) => {
    if (a.count !== b.count) return b.count - a.count;
    if (a.latest_at !== b.latest_at) return a.latest_at < b.latest_at ? 1 : -1;
    return 0;
  });
}

/**
 * Convenience: the maximum bucket count across the histogram. The
 * renderer uses this as the bar's denominator so the largest bucket
 * fills the row. Returns 0 on an empty histogram (the renderer guards
 * the divide).
 */
export function maxHistogramCount(buckets: readonly HistogramBucket[]): number {
  let max = 0;
  for (const b of buckets) if (b.count > max) max = b.count;
  return max;
}
